/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, ComponentProps, RefObject } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import {
  AriaAttributesSignalExcluded,
  HTMLAttributesSignalExcluded
} from '#utils/UNSAFE_attributeUtils';
import { Flex } from '#UNSAFE_Flex';
import { FocusTrap } from '#UNSAFE_FocusTrap';
import { KEYS } from '#utils/PRIVATE_keyboardUtils';
import { PaddingProps } from '#utils/UNSAFE_interpolations/padding';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { View } from '#UNSAFE_View';

type FlexProps = ComponentProps<typeof Flex>;

type Props = TestIdProps & {
  /**
   * An accessible label that describes the content.
   */
  'aria-label'?: AriaAttributesSignalExcluded['aria-label'];

  /**
   * A ref to the content container.
   */
  contentRef?: RefObject<HTMLDivElement>;

  /**
   * Child content in the footer section.
   */
  footer?: ComponentChildren;

  /**
   * The spacing between the sections.
   */
  gap?: FlexProps['gap'];

  /**
   * True if the tab key should cycle focus within the content.
   * @default false
   */
  hasFocusTrap?: boolean;

  /**
   * Child content in the header section.
   */
  header?: ComponentChildren;

  /**
   * Child content in the main section.
   */
  main?: ComponentChildren;

  /**
   * The padding between the footer or bottom edge and the main content.
   * @default '4x'
   */
  paddingBlockEnd?: PaddingProps['paddingBlockEnd'];

  /**
   * The padding between the header or top edge and the main content.
   * @default '4x'
   */
  paddingBlockStart?: PaddingProps['paddingBlockStart'];

  /**
   * The padding between the right edge and the main content.
   * @default '4x'
   */
  paddingInlineEnd?: PaddingProps['paddingInlineEnd'];

  /**
   * The padding between the left edge and the main content.
   * @default '4x'
   */
  paddingInlineStart?: PaddingProps['paddingInlineStart'];

  /**
   * The aria role applied to the content container. For example, if the "launcher" element
   * includes aria-haspopup="dialog", then the associated content should have role "dialog"
   * and an aria-label that describes it. If the launcher element has role "combobox", then
   * it controls an element with a role such as listbox or grid.
   */
  role?: HTMLAttributesSignalExcluded['role'];
};

/**
 * The SectionedContent component has slots for header, main, and footer content.
 * It is meant to be used in a floating or modal type container and includes
 * conditional support for focus trapping, so that you can tab between components
 * and sections while the container remains open.
 */
export const SectionedContent = ({
  'aria-label': ariaLabel,
  contentRef: propRef,
  gap,
  footer,
  hasFocusTrap = false,
  header,
  main,
  paddingBlockEnd = '4x',
  paddingBlockStart = '4x',
  paddingInlineEnd = '4x',
  paddingInlineStart = '4x',
  role,
  testId
}: Props) => {
  const testIdProps = useTestId(testId);

  const cRef = useRef<HTMLDivElement>(null);
  const contentRef = propRef ?? cRef;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.code === KEYS.TAB) {
      event.stopPropagation();
    }
  }, []);

  const sections = (
    <Flex direction="column" gap={gap}>
      {header}
      <View
        paddingBlockEnd={paddingBlockEnd}
        paddingBlockStart={paddingBlockStart}
        paddingInlineEnd={paddingInlineEnd}
        paddingInlineStart={paddingInlineStart}>
        {main}
      </View>
      {footer}
    </Flex>
  );

  // Use a FocusTrap around the content if hasFocusTrap is true.
  const childContent = hasFocusTrap ? (
    <FocusTrap restoreFocusRef={false}>{sections}</FocusTrap>
  ) : (
    { sections }
  );

  return (
    <div
      aria-label={ariaLabel}
      onKeyDown={hasFocusTrap ? handleKeyDown : undefined}
      ref={contentRef}
      role={role}
      {...testIdProps}>
      {childContent}
    </div>
  );
};
